import { MODEL_MOTION, turnSeconds, walkAt, walkSeconds } from './model-motion.js';
/**
 * A model going round (ADR-054; docs/superpowers/specs/2026-10-10-rondjes-en-hoogte-design.md): the
 * circle's geometry and timing, computed analytically, which the validator samples and the headset's
 * engine follows -- one function, so the Quest plays what the checks judged. Pure, and importing only
 * types from models.ts (models.ts imports this). A later path (through points, an eight) is added
 * beside it, reusing `Track` (track.ts), without new engine concepts (decision 8).
 *
 * Ground points are [x, z]; turns and angles are degrees in the rig's yaw convention (0 faces −z,
 * positive turns left; `modelForward(t)` = [−sin t, −cos t]). A ring's point at `angle` is
 * centre + R · modelForward(angle): `links` (counter-clockwise seen from above) increases the angle and
 * faces angle + 90, `rechts` decreases it and faces angle − 90.
 */
export const MODEL_CIRCLE = {
    /** Rounds: 0.25–4 in quarters, or "loop". */
    roundsMin: 0.25,
    roundsMax: 4,
    roundsStep: 0.25,
    /** A finite circle takes at most this, turn-in included: one narration. Longer is "loop". */
    secondsMax: 45,
    /** As the pupil sees it, degrees per second (decision 9; to confirm on the Quest). */
    angularMax: 15,
    /** A raised model's top at most this many degrees above the eyes' horizon (decision 9). */
    upMax: 35,
    /** `above`, metres. */
    aboveMax: 20,
    /** Models going round at once (v1). */
    circlingMax: 1,
    /** Models moving at once: walking or going round. */
    movingMax: 2,
    /** The ring is checked every this many metres: the distance rule's walk sample. */
    sample: 0.25,
};
export const CIRCLE_DIRECTIONS = ['links', 'rechts'];
const DEG = Math.PI / 180;
/** Degrees in (−180, 180]. */
function normalise(degrees) {
    const d = ((((degrees + 180) % 360) + 360) % 360) - 180;
    return d === -180 ? 180 : d + 0;
}
const sign = (circle) => circle.direction === 'links' ? 1 : -1;
/**
 * The circle a model standing at `start` makes round `centre`; undefined when it stands on the centre
 * (no radius). The radius is never written: it is where the model is (spec, "The format").
 */
export function circleOf(input) {
    const dx = input.start[0] - input.centre[0];
    const dz = input.start[1] - input.centre[1];
    const radius = Math.hypot(dx, dz);
    if (!(radius > 1e-6))
        return undefined;
    const startAngle = normalise((Math.atan2(-dx, -dz) * 180) / Math.PI);
    const circle = { direction: input.direction };
    return {
        centre: [input.centre[0], input.centre[1]],
        radius,
        direction: input.direction,
        rounds: input.rounds,
        startAngle,
        start: [input.start[0], input.start[1]],
        heading: normalise(startAngle + sign(circle) * 90),
        gait: input.gait,
        ...(input.speed !== undefined && input.speed > 0 ? { speed: input.speed } : {}),
    };
}
/** How far it goes, metres: 2πR × rounds; Infinity for a loop. */
export function circleLength(circle) {
    return circle.rounds === 'loop' ? Infinity : 2 * Math.PI * circle.radius * circle.rounds;
}
/** Where it is `s` metres along, and its turn there (along the ring). */
export function circlePoint(circle, s) {
    const angle = circle.startAngle + (sign(circle) * (s / circle.radius)) / DEG;
    const r = angle * DEG;
    return {
        pos: [
            circle.centre[0] - circle.radius * Math.sin(r),
            circle.centre[1] - circle.radius * Math.cos(r),
        ],
        turn: normalise(angle + sign(circle) * 90),
    };
}
/** Where a finite circle ends, turned along the ring; undefined for a loop. */
export function circleEnd(circle) {
    return circle.rounds === 'loop' ? undefined : circlePoint(circle, circleLength(circle));
}
/**
 * How far along it is `t` seconds into the arc, and how fast: up to its loop's speed over 0.8 s; a
 * finite circle brakes at 5 m/s² to stop at its end (`walkAt`), a loop goes on.
 */
export function circleTravelled(circle, t) {
    const speed = circle.speed ?? 0;
    if (!(speed > 0) || t <= 0)
        return { distance: 0, speed: 0 };
    if (circle.rounds !== 'loop')
        return walkAt(t, circleLength(circle), speed);
    const ramp = MODEL_MOTION.ramp;
    if (t < ramp)
        return { distance: ((speed / ramp) * t * t) / 2, speed: (speed / ramp) * t };
    return { distance: (speed * ramp) / 2 + speed * (t - ramp), speed };
}
/** Seconds of the arc: a finite circle's ramp, run and brake; Infinity for a loop; 0 without a speed. */
export function circleArcSeconds(circle) {
    if (circle.rounds === 'loop')
        return Infinity;
    return circle.speed === undefined ? 0 : walkSeconds(circleLength(circle), circle.speed);
}
/**
 * Seconds an entry that goes round takes, as a step counts it (`stepSecondsWithModels`): the turn on
 * the spot to the ring (90°/s) and, for a finite circle, its arc. A loop counts only its turn-in.
 */
export function circleSeconds(circle, turnFrom) {
    const turnIn = turnSeconds(turnFrom, circle.heading);
    return circle.rounds === 'loop' ? turnIn : turnIn + circleArcSeconds(circle);
}
/**
 * Where it is `t` seconds into the arc (after the turn-in), its turn, its ground speed (the clip plays
 * at `clipTimeScale(speed, its loop's speed)`), and whether a finite circle is over. The engine follows
 * this function frame by frame.
 */
export function circleAt(circle, t) {
    const done = circle.rounds !== 'loop' && t >= circleArcSeconds(circle);
    if (done)
        return { ...circlePoint(circle, circleLength(circle)), speed: 0, done };
    const travelled = circleTravelled(circle, t);
    return { ...circlePoint(circle, travelled.distance), speed: travelled.speed, done };
}
/**
 * Places along it every 0.25 m, both ends included, each turned along the ring: the whole ring for a
 * loop or a circle of a round or more, else its arc. What the distance rule judges.
 */
export function circleSamples(circle) {
    const whole = circle.rounds === 'loop' || circle.rounds >= 1;
    const length = whole ? 2 * Math.PI * circle.radius : circleLength(circle);
    const n = Math.max(1, Math.ceil(length / MODEL_CIRCLE.sample - 1e-9));
    return Array.from({ length: n + 1 }, (_, k) => circlePoint(circle, (length * k) / n));
}
/**
 * How fast it turns as seen from `eye`, degrees per second, at its fastest: its loop's speed over the
 * nearest the eyes come to the ring's path (v / R from the centre). 0 without a speed.
 */
export function circleAngularSpeed(circle, eye) {
    const speed = circle.speed ?? 0;
    if (!(speed > 0))
        return 0;
    const nearest = Math.abs(Math.hypot(eye[0] - circle.centre[0], eye[1] - circle.centre[1]) - circle.radius);
    return nearest <= 1e-9 ? Infinity : speed / nearest / DEG;
}
//# sourceMappingURL=model-path.js.map